import React from 'react';
import { useIntl } from 'react-intl';
import { DashboardWidget } from './DashboardWidget';

interface ICycleStatus {
  clientId: number;
  clientName: string;
  // Sesiones restantes del ciclo activo. null = el cliente NO está por quedarse
  // sin entrenamientos (le queda > 25% de las sesiones y la última sesión
  // agendada está a más de 1 semana).
  sessionsLeft: number | null;
  totalSessions: number;
  // Día (y entrenamiento) de la última sesión agendada = cuándo se queda sin
  // entrenamientos. Puede faltar en respuestas viejas de caché.
  lastSessionDate?: string | null;
  lastSessionWorkoutName?: string | null;
  /** #6 ítem 4: con esto el nombre del entrenamiento abre el preview. */
  lastSessionWorkoutInstanceId?: number | null;
}

interface Props {
  data: ICycleStatus[];
  loading: boolean;
  onClientClick?: (clientId: number) => void;
  onWorkoutClick?: (workoutInstanceId: number) => void;
}

export const RunningOutWidget: React.FC<Props> = ({ data, loading, onClientClick, onWorkoutClick }) => {
  const intl = useIntl();

  const clientsRunningOut = data
    .filter((c) => c.sessionsLeft !== null && c.sessionsLeft !== undefined)
    .sort((a, b) => (a.sessionsLeft ?? 999) - (b.sessionsLeft ?? 999));

  const getColor = (left: number) => {
    if (left <= 1) return '#ef4444';
    if (left <= 2) return '#f97316';
    return '#f59e0b';
  };

  return (
    <DashboardWidget
      title={intl.formatMessage({ id: 'dashboard.runningOut' })}
      icon="pi pi-exclamation-triangle"
      iconColor="#f97316"
      loading={loading}
      badge={clientsRunningOut.length || null}
    >
      {clientsRunningOut.length === 0 ? (
        <p style={{ color: 'var(--ios-text-tertiary)', fontSize: '0.85rem', textAlign: 'center', padding: '1rem 0' }}>
          {intl.formatMessage({ id: 'dashboard.noCycleData' })}
        </p>
      ) : (
        <div className="flex flex-column gap-2">
          {clientsRunningOut.slice(0, 6).map((client) => {
            const left = client.sessionsLeft as number;
            const cutoffDate = client.lastSessionDate
              ? new Date(client.lastSessionDate).toLocaleDateString(intl.locale, { day: 'numeric', month: 'short' })
              : null;
            return (
              <div
                key={client.clientId}
                style={{
                  padding: '0.5rem',
                  borderRadius: 'var(--ios-radius-md)',
                  background: 'var(--ios-surface-subtle)',
                  borderLeft: `3px solid ${getColor(left)}`
                }}
              >
                <div className="flex align-items-center justify-content-between">
                  {/* #6 ítem 4: nombre de la persona -> perfil. */}
                  <button
                    type="button"
                    onClick={() => onClientClick?.(client.clientId)}
                    style={{
                      background: 'transparent',
                      border: 'none',
                      padding: 0,
                      textAlign: 'left',
                      fontFamily: 'inherit',
                      fontWeight: 600,
                      fontSize: '0.82rem',
                      color: 'var(--ios-text)',
                      cursor: onClientClick ? 'pointer' : 'default'
                    }}
                  >
                    {client.clientName}
                  </button>
                  <span style={{ fontWeight: 700, fontSize: '0.78rem', color: getColor(left) }}>
                    {left}/{client.totalSessions} {intl.formatMessage({ id: 'dashboard.sessionsLeftLabel' })}
                  </span>
                </div>
                {cutoffDate && (
                  <div
                    // #6 ítem 2: más aire entre el nombre del alumno y el del entrenamiento.
                    style={{ fontSize: '0.72rem', color: 'var(--ios-text-tertiary)', marginTop: '0.35rem' }}
                  >
                    <span>{intl.formatMessage({ id: 'dashboard.runsOutOn' }, { date: cutoffDate })}</span>
                    {client.lastSessionWorkoutName && (
                      <>
                        {' · '}
                        {/* #6 ítem 4: nombre del entrenamiento -> preview. */}
                        {client.lastSessionWorkoutInstanceId && onWorkoutClick ? (
                          <button
                            type="button"
                            onClick={() => onWorkoutClick(client.lastSessionWorkoutInstanceId as number)}
                            style={{
                              background: 'transparent',
                              border: 'none',
                              padding: 0,
                              fontFamily: 'inherit',
                              fontSize: '0.72rem',
                              color: 'var(--ios-text-tertiary)',
                              cursor: 'pointer',
                              textDecoration: 'underline',
                              textDecorationStyle: 'dotted',
                              textUnderlineOffset: '2px'
                            }}
                          >
                            {client.lastSessionWorkoutName}
                          </button>
                        ) : (
                          <span>{client.lastSessionWorkoutName}</span>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </DashboardWidget>
  );
};
